import { Box3, Group, Vector3 } from "three";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { ModelAsset } from "../models/model-assets";

export type RollerConveyorOptions = {
  length?: number;
  position?: { x?: number; y?: number; z?: number };
  rotationY?: number;
  sideRailEndInset?: number;
  sideRailXInset?: number;
  sideRailYOffset?: number;
  width?: number;
};

type AlignedPart = {
  content: Group;
  object: Group;
  size: Vector3;
};

const defaultOptions = {
  length: 1.701,
  rotationY: 0,
  sideRailEndInset: 0.02,
  sideRailXInset: 0.07,
  sideRailYOffset: -0.11,
  width: 1.3314
};
const backRotationY = -Math.PI / 2;
const frontRotationY = Math.PI / 2;
const rollerRotationY = Math.PI / 2;

export class RollerConveyor extends Group {
  private readonly length: number;
  private sideRailBaseX = 0;
  private sideRailBaseY = 0;
  private sideRailEndInset: number;
  private sideRailOriginalLength = 0;
  private sideRailXInset: number;
  private sideRailYOffset: number;
  private readonly structure = new Group();
  private readonly width: number;

  constructor({
    length = defaultOptions.length,
    position = {},
    rotationY = defaultOptions.rotationY,
    sideRailEndInset = defaultOptions.sideRailEndInset,
    sideRailXInset = defaultOptions.sideRailXInset,
    sideRailYOffset = defaultOptions.sideRailYOffset,
    width = defaultOptions.width
  }: RollerConveyorOptions = {}) {
    super();
    this.length = validatePositiveNumber(length, "length");
    this.width = validatePositiveNumber(width, "width");
    this.sideRailEndInset = validateNonNegativeNumber(sideRailEndInset, "sideRailEndInset");
    this.sideRailXInset = validateNonNegativeNumber(sideRailXInset, "sideRailXInset");
    this.sideRailYOffset = validateFiniteNumber(sideRailYOffset, "sideRailYOffset");
    if (!Number.isFinite(rotationY)) {
      throw new RangeError("rotationY must be finite");
    }

    this.name = "roller-conveyor";
    this.structure.name = "roller-conveyor-structure";
    this.position.set(position.x ?? 0, position.y ?? 0, position.z ?? 0);
    this.rotation.y = rotationY;
    this.add(this.structure);
  }

  static async create(
    options: RollerConveyorOptions = {},
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    const conveyor = new RollerConveyor(options);
    await conveyor.loadStructure(loader);
    return conveyor;
  }

  getSideRailXInset() {
    return this.sideRailXInset;
  }

  getSideRailEndInset() {
    return this.sideRailEndInset;
  }

  getSideRailYOffset() {
    return this.sideRailYOffset;
  }

  setSideRailEndInset(endInset: number) {
    this.sideRailEndInset = validateNonNegativeNumber(endInset, "sideRailEndInset");
    if (this.sideRailOriginalLength <= 0) {
      return;
    }
    const scaleX =
      Math.max(0, this.width - this.sideRailEndInset * 2) / this.sideRailOriginalLength;
    this.structure.children
      .filter(({ name }) => name.startsWith("roller-conveyor-side-rail-"))
      .forEach((sideRail) => {
        sideRail.children[0]?.scale.setX(scaleX);
      });
  }

  setSideRailXInset(xInset: number) {
    this.sideRailXInset = validateNonNegativeNumber(xInset, "sideRailXInset");
    this.structure.children
      .filter(({ name }) => name.startsWith("roller-conveyor-side-rail-"))
      .forEach((sideRail) => {
        const direction = sideRail.name.endsWith("-left") ? -1 : 1;
        sideRail.position.x = direction * (this.sideRailBaseX - this.sideRailXInset);
      });
  }

  setSideRailYOffset(yOffset: number) {
    this.sideRailYOffset = validateFiniteNumber(yOffset, "sideRailYOffset");
    this.structure.children
      .filter(({ name }) => name.startsWith("roller-conveyor-side-rail-"))
      .forEach((sideRail) => {
        sideRail.position.y = this.sideRailBaseY + this.sideRailYOffset;
      });
  }

  private async loadStructure(loader: ModelAssetLoader) {
    const [
      backLeft,
      backRight,
      frontLeft,
      frontRight,
      leftRail,
      rightRail,
      backCrossbar,
      frontCrossbar
    ] = await Promise.all([
      loadAlignedPart(
        loader,
        ModelAsset.RollerConveyorPost,
        "roller-conveyor-post-back-left",
        "bottom"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.RollerConveyorPost,
        "roller-conveyor-post-back-right",
        "bottom"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.RollerConveyorPost,
        "roller-conveyor-post-front-left",
        "bottom"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.RollerConveyorPost,
        "roller-conveyor-post-front-right",
        "bottom"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.RollerConveyorSideRail,
        "roller-conveyor-side-rail-left",
        "center"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.RollerConveyorSideRail,
        "roller-conveyor-side-rail-right",
        "center"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.RollerConveyorCrossbar,
        "roller-conveyor-crossbar-back",
        "center"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.RollerConveyorCrossbar,
        "roller-conveyor-crossbar-front",
        "center"
      )
    ]);

    const posts = [backLeft, backRight, frontLeft, frontRight];
    const postWidth = backLeft.size.x;
    const postDepth = backLeft.size.z;
    const halfPostSpanX = Math.max(0, this.length / 2 - postDepth / 2);
    const halfPostSpanZ = Math.max(0, this.width / 2 - postWidth / 2);
    [
      { part: backLeft, rotationY: backRotationY, x: -halfPostSpanX, z: -halfPostSpanZ },
      { part: backRight, rotationY: backRotationY, x: halfPostSpanX, z: -halfPostSpanZ },
      { part: frontLeft, rotationY: frontRotationY, x: -halfPostSpanX, z: halfPostSpanZ },
      { part: frontRight, rotationY: frontRotationY, x: halfPostSpanX, z: halfPostSpanZ }
    ].forEach(({ part, rotationY, x, z }) => {
      part.object.position.set(x, 0, z);
      part.content.rotation.y = rotationY;
    });

    this.sideRailBaseY = backLeft.size.y + leftRail.size.y / 2;
    this.sideRailBaseX = Math.max(0, this.length / 2 - leftRail.size.z / 2);
    this.sideRailOriginalLength = leftRail.size.x;
    const railScaleX =
      Math.max(0, this.width - this.sideRailEndInset * 2) / this.sideRailOriginalLength;
    [leftRail, rightRail].forEach((rail, index) => {
      const isLeft = index === 0;
      const direction = isLeft ? -1 : 1;
      rail.object.position.set(
        direction * (this.sideRailBaseX - this.sideRailXInset),
        this.sideRailBaseY + this.sideRailYOffset,
        0
      );
      rail.content.rotation.y = isLeft ? Math.PI / 2 : -Math.PI / 2;
      rail.object.rotation.z = isLeft ? Math.PI / 2 : -Math.PI / 2;
      rail.content.scale.x = railScaleX;
    });

    const crossbarCenterY = backLeft.size.y + backCrossbar.size.y / 2;
    const halfCrossbarSpanZ = Math.max(0, this.width / 2 - backCrossbar.size.x / 2);
    const crossbarScaleZ = this.length / backCrossbar.size.z;
    [backCrossbar, frontCrossbar].forEach((crossbar, index) => {
      const isBack = index === 0;
      crossbar.object.position.set(
        0,
        crossbarCenterY,
        isBack ? -halfCrossbarSpanZ : halfCrossbarSpanZ
      );
      crossbar.content.rotation.y = isBack ? -Math.PI / 2 : Math.PI / 2;
      crossbar.content.scale.z = crossbarScaleZ;
    });

    const rollerTemplate = await loadAlignedPart(
      loader,
      ModelAsset.RollerConveyorRoller,
      "roller-conveyor-roller-template",
      "center"
    );
    const rollerDiameter = rollerTemplate.size.z;
    const rollerTargetLength = Math.max(0, this.width - backCrossbar.size.x * 2);
    const rollerScaleX = rollerTargetLength / rollerTemplate.size.x;
    const rollerCenterY = crossbarCenterY;
    const rollerStartX = -this.length / 2 + rollerDiameter / 2;
    const rollerEndX = this.length / 2 - rollerDiameter / 2;
    const rollerPositions = createCenteredPositions(rollerStartX, rollerEndX, rollerDiameter * 2.5);
    const rollers = await Promise.all(
      rollerPositions.map(async (x, index) => {
        const roller =
          index === 0
            ? rollerTemplate
            : await loadAlignedPart(
                loader,
                ModelAsset.RollerConveyorRoller,
                `roller-conveyor-roller-${index}`,
                "center"
              );
        roller.object.name = `roller-conveyor-roller-${index}`;
        roller.object.position.set(x, rollerCenterY, 0);
        roller.content.rotation.y = rollerRotationY;
        roller.content.scale.x = rollerScaleX;
        return roller.object;
      })
    );

    this.structure.add(
      ...posts.map(({ object }) => object),
      leftRail.object,
      rightRail.object,
      backCrossbar.object,
      frontCrossbar.object,
      ...rollers
    );
  }
}

function createCenteredPositions(start: number, end: number, pitch: number) {
  if (end <= start || pitch <= 0) {
    return [(start + end) / 2];
  }
  const count = Math.floor((end - start) / pitch) + 1;
  if (count <= 1) {
    return [(start + end) / 2];
  }
  const firstPosition = (start + end - pitch * (count - 1)) / 2;
  return Array.from({ length: count }, (_, index) => firstPosition + pitch * index);
}

async function loadAlignedPart(
  loader: ModelAssetLoader,
  asset: ModelAsset,
  name: string,
  origin: "bottom" | "center"
): Promise<AlignedPart> {
  const model = await loader.load(asset);
  model.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(model);
  const center = bounds.getCenter(new Vector3());
  const size = bounds.getSize(new Vector3());
  const object = new Group();
  const content = new Group();

  model.position.set(
    model.position.x - center.x,
    model.position.y - (origin === "bottom" ? bounds.min.y : center.y),
    model.position.z - center.z
  );
  object.name = name;
  content.add(model);
  object.add(content);
  return { content, object, size };
}

function validatePositiveNumber(value: number, label: string) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${label} must be a positive finite number`);
  }
  return value;
}

function validateFiniteNumber(value: number, label: string) {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${label} must be finite`);
  }
  return value;
}

function validateNonNegativeNumber(value: number, label: string) {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${label} must be a non-negative finite number`);
  }
  return value;
}
